import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState('');
  const [content, setContent] = useState('');
  const [isVisibleInFooter, setIsVisibleInFooter] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [isCreating, setIsCreating] = useState(false);
  const [newPageName, setNewPageName] = useState('');

  const fetchPagesList = React.useCallback(async () => {
    try {
      const res = await api.get('/pages');
      if (res.data && res.data.success) {
        const fetchedPages = res.data.data.map(p => ({ id: p.slug, name: p.title }));
        setPages(fetchedPages);
      }
    } catch (err) {
      console.error("Failed to fetch pages list", err);
    }
  }, []);

  useEffect(() => {
    fetchPagesList();
  }, [fetchPagesList]);

  // Handle initial selection
  useEffect(() => {
    if (pages.length > 0 && !selectedPage) {
      setSelectedPage(pages[0].id);
    }
  }, [pages, selectedPage]);

  useEffect(() => {
    if (selectedPage) {
      fetchPageContent(selectedPage);
    }
  }, [selectedPage]);

  const fetchPageContent = async (slug) => {
    setLoading(true);
    try {
      const res = await api.get(`/pages/${slug}`);
      if (res.data && res.data.data) {
        setContent(res.data.data.content || '');
        setIsVisibleInFooter(res.data.data.isVisibleInFooter !== false);
      } else {
        setContent('');
        setIsVisibleInFooter(true);
      }
    } catch (err) {
      console.error(err);
      setContent('');
      setIsVisibleInFooter(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewPage = () => {
    if(!newPageName.trim()) return;
    const slug = newPageName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
    
    // Check if slug already exists locally
    if (pages.some(p => p.id === slug)) {
      alert("A page with a similar name already exists!");
      return;
    }

    const newPage = { id: slug, name: newPageName.trim() };
    setPages([...pages, newPage]);
    setSelectedPage(slug);
    setContent(`<h1>${newPageName.trim()}</h1><p>Start writing here...</p>`);
    setIsVisibleInFooter(true);
    setNewPageName('');
    setIsCreating(false);
  };

  const savePageContent = async () => {
    if (!selectedPage) return;
    setSaving(true);
    try {
      const title = pages.find(p => p.id === selectedPage)?.name || selectedPage;
      await api.put(`/pages/${selectedPage}`, { title, content, isVisibleInFooter });
      alert('✅ Page saved successfully!');
      // Refresh list to ensure we have the latest
      fetchPagesList();
    } catch (err) {
      console.error(err);
      alert('Failed to save page.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePage = async (slug) => {
    if (!window.confirm(`Are you sure you want to delete the page "${slug}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/pages/${slug}`);
      alert('✅ Page deleted successfully!');
      
      // Update local state
      const updatedPages = pages.filter(p => p.id !== slug);
      setPages(updatedPages);
      
      if (selectedPage === slug) {
        if (updatedPages.length > 0) {
          setSelectedPage(updatedPages[0].id);
        } else {
          setSelectedPage('');
          setContent('');
        }
      }
    } catch (err) {
      console.error(err);
      alert('Failed to delete page.');
    }
  };

  return (
    <div className="p-8 relative">
      <style>{`
        .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
        .ql-editor {
          font-family: inherit;
          font-size: 1rem;
        }
      `}</style>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="font-serif text-3xl font-bold text-[#2A2858]">CMS Pages</h2>
          <p className="mt-1 text-stone-500 text-sm">Manage dynamic content for frontend pages</p>
        </div>
      </div>

      <div className="flex gap-8">
        <div className="w-1/4">
          <div className="bg-white rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E8B84B]/20 flex flex-col gap-2">
            <div className="flex items-center justify-between mb-2 px-2">
              <h3 className="font-bold text-stone-700 uppercase tracking-widest text-xs">Available Pages</h3>
              <button 
                onClick={() => setIsCreating(!isCreating)}
                className="text-[#2A2858] hover:bg-stone-100 p-1 rounded transition-colors"
                title="Create New Page"
              >
                <span className="material-symbols-outlined text-[18px]">add</span>
              </button>
            </div>

            {isCreating && (
              <div className="p-3 bg-stone-50 rounded-xl mb-2 border border-stone-200">
                <input 
                  type="text" 
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                  placeholder="New Page Name"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-stone-200 focus:outline-none focus:border-[#E8B84B] mb-2"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button onClick={handleCreateNewPage} className="flex-1 bg-[#2A2858] text-white text-xs py-1.5 rounded-lg font-bold">Add</button>
                  <button onClick={() => setIsCreating(false)} className="flex-1 bg-stone-200 text-stone-700 text-xs py-1.5 rounded-lg font-bold">Cancel</button>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1 max-h-[500px] overflow-y-auto">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className={`flex items-center justify-between group rounded-xl transition-all ${
                    selectedPage === page.id
                      ? 'bg-[#2A2858] text-white shadow-md'
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <button
                    onClick={() => setSelectedPage(page.id)}
                    className="flex-1 text-left px-4 py-3 font-bold text-sm"
                  >
                    {page.name}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePage(page.id);
                    }}
                    className={`px-3 py-3 transition-opacity ${
                      selectedPage === page.id ? 'text-white/70 hover:text-white' : 'text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100'
                    }`}
                    title="Delete Page"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
              ))}
              {pages.length === 0 && !isCreating && (
                <p className="text-xs text-stone-400 text-center py-4">No pages found.</p>
              )}
            </div>
          </div>
        </div>

        <div className="w-3/4">
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E8B84B]/20 flex flex-col h-[700px]">
            {selectedPage ? (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-stone-700 text-lg">Edit Content: {pages.find(p => p.id === selectedPage)?.name}</h3>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 bg-stone-50 px-4 py-2 rounded-xl border border-stone-100">
                      <span className="text-sm font-bold text-stone-600">Visible in Footer</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={isVisibleInFooter} 
                          onChange={(e) => setIsVisibleInFooter(e.target.checked)} 
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-stone-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2A2858]"></div>
                      </label>
                    </div>

                    <button 
                      onClick={savePageContent}
                      disabled={saving || loading || !selectedPage}
                      className="bg-[#2A2858] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-[#14122B] transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : <><span className="material-symbols-outlined text-sm font-bold">save</span> Save Page</>}
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="flex-1 flex items-center justify-center text-stone-400">Loading...</div>
                ) : (
                  <div className="flex-1 overflow-hidden flex flex-col">
                    <ReactQuill
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      className="flex-1 h-[500px] mb-12"
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'color': [] }, { 'background': [] }],
                          [{ 'align': [] }],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['link', 'image', 'video'],
                          ['clean']
                        ]
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-stone-400">
                <span className="material-symbols-outlined text-4xl mb-2 opacity-50">description</span>
                <p>Select or create a page to edit content</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagesManager;
